import { CreditCardOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Button,Flex,StatusText,Tooltip,Typography } from "../../../../../../../shared/frontend/ui";
import "./personOverview.css";
import React from "react";
import { accessMediumTypeLabel } from "./personAccessMediumUtils";
import type { AccessMediumRow } from "./personDetailTypes";

type PersonAccessMediaOverviewSectionProps = {
  api: {
    get: <T>(url: string) => Promise<T>;
    del: (url: string) => Promise<unknown>;
  };
  personId: string;
  subjectKind?: "person" | "account";
  text: (key: string) => string;
  canWriteAccessMedia: boolean;
  /** Nur `reload` wird gesetzt und aufgerufen — bewusst schmal statt der vollen Listen-Ref. */
  accessMediaRef: React.RefObject<{ reload?: () => void } | null>;
  modal: {
    confirm: (config: {
      title: React.ReactNode;
      content?: React.ReactNode;
      okText?: React.ReactNode;
      okButtonProps?: { danger?: boolean };
      cancelText?: React.ReactNode;
      onOk?: () => void;
      onCancel?: () => void;
    }) => void;
  };
  message: {
    success: (content: React.ReactNode) => void;
  };
  onAdd: () => void;
  /** Hinzufügen unter der Liste anbieten; `false`, wenn die umgebende Karte die Aktion im Kopf trägt. */
  showAddButton?: boolean;
  /** Anzahl nach jedem Laden — für eine Kennzahl, die die umgebende Karte selbst zeigt. */
  onCountChange?: (count: number) => void;
  /** Ohne Medien nichts rendern; die Kennzahl „0“ der umgebenden Karte ist dann die Aussage. */
  hideEmpty?: boolean;
};

export function PersonAccessMediaOverviewSection({
  api,
  personId,
  subjectKind = "person",
  text,
  canWriteAccessMedia,
  accessMediaRef,
  modal,
  message,
  onAdd,
  showAddButton = true,
  onCountChange,
  hideEmpty = false
}: PersonAccessMediaOverviewSectionProps) {
  const [accessMediaList, setAccessMediaList] = React.useState<AccessMediumRow[]>([]);
  const [accessMediaRefreshKey, setAccessMediaRefreshKey] = React.useState(0);

  const loadAccessMedia = React.useCallback(() => {
    if (!personId) return;
    api
      .get<Array<Record<string, unknown>>>(
        subjectKind === "account"
          ? `/api/v1/access-media?accountId=${encodeURIComponent(personId)}`
          : `/api/v1/access-media?personId=${encodeURIComponent(personId)}`
      )
      .then((list) => {
        const rows: AccessMediumRow[] = (list || []).map((x) => ({
          id: String(x.id),
          type: Number(x.type || 0),
          mediumId: String(x.mediumId || ""),
          isActive: x.isActive !== false,
          createdAt: String(x.createdAt || "")
        }));
        setAccessMediaList(rows);
        onCountChange?.(rows.length);
      })
      .catch(() => setAccessMediaList([]));
  }, [personId, api, subjectKind, onCountChange]);

  React.useEffect(() => {
    loadAccessMedia();
  }, [loadAccessMedia, accessMediaRefreshKey]);

  React.useEffect(() => {
    accessMediaRef.current = {
      reload: () => setAccessMediaRefreshKey((prev) => prev + 1)
    };
  }, [accessMediaRef]);

  const deleteAccessMedium = React.useCallback(
    async (item: AccessMediumRow) => {
      const ok = await new Promise<boolean>((resolve) => {
        modal.confirm({
          title: text("Delete access medium?"),
          content: text("Do you really want to delete this access medium?"),
          okText: text("Delete"),
          okButtonProps: { danger: true },
          cancelText: text("Cancel"),
          onOk: () => resolve(true),
          onCancel: () => resolve(false)
        });
      });
      if (!ok) return;
      try {
        await api.del(`/api/v1/access-media/${item.id}`);
        message.success(text("Access medium deleted."));
        setAccessMediaRefreshKey((prev) => prev + 1);
      } catch {
        // Central API toast shows error
      }
    },
    [api, message, modal, text]
  );

  if (hideEmpty && accessMediaList.length === 0 && !(canWriteAccessMedia && showAddButton)) return null;
  return (
    <Flex vertical gap="small" style={{ width: "100%" }}>
      {accessMediaList.length === 0 ? (
        <Typography.Text type="secondary">{text("No access media")}</Typography.Text>
      ) : (
        // Aufzählung als Text statt Etikettenreihe (DNA D29); nur der Ausnahmezustand „inaktiv“ bekommt eine Markierung.
        <ul className="person-access-media-list">
          {accessMediaList.map((item) => (
            <li key={item.id} className="person-access-media-row">
              <span className="person-access-media-label">
                <CreditCardOutlined aria-hidden />
                <Typography.Text>{accessMediumTypeLabel(item.type, text)}: {item.mediumId || "—"}</Typography.Text>
                {item.isActive ? null : <StatusText>{text("Inactive")}</StatusText>}
              </span>
              {canWriteAccessMedia ? (
                <Tooltip title={text("Delete")}>
                  <Button
                    type="text"
                    size="small"
                    icon={<DeleteOutlined />}
                    aria-label={text("Delete")}
                    onClick={() => { return deleteAccessMedium(item).catch(() => {}); }}
                  />
                </Tooltip>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {canWriteAccessMedia && showAddButton ? (
        <div>
          <Button size="small" icon={<PlusOutlined />} onClick={onAdd}>
            {text("Add access medium")}
          </Button>
        </div>
      ) : null}
    </Flex>
  );
}
